import React, { useEffect, useState } from "react";
import { db } from "../../firebaseConfig"; // Your Firebase configuration file
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { debounce } from "lodash";

// Define the User interface
interface Deposit {
  amount: number;
  date: any; // Replace with a more specific type if possible
  transactionId: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  deposit: Deposit;
  status: string;
}

const AdminDashboardWithDeposits: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch users with deposits
  useEffect(() => {
    const fetchUsersWithDeposits = async () => {
      try {
        const userCollection = collection(db, "users");
        const snapshot = await getDocs(userCollection);

        const userList = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              username: data.username,
              email: data.email,
              deposit: data.deposit,
              status: data.status,
            };
          })
          .filter((user) => user.deposit && user.deposit.transactionId);

        setUsers(userList);
        setFilteredUsers(userList);
      } catch (error) {
        console.error("Error fetching users with deposits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsersWithDeposits();
  }, []);

  // Handle toggle of verified/unverified status
  const toggleVerifyStatus = async (userId: string, currentStatus: string) => {
    const userRef = doc(db, "users", userId);
    try {
      await updateDoc(userRef, {
        status: currentStatus === "verified" ? "unverified" : "verified",
      });

      // Optimistic update of status in UI
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? { ...user, status: currentStatus === "verified" ? "unverified" : "verified" }
            : user
        )
      );
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  // Handle search functionality with debounce
  const handleSearch = debounce((term: string) => {
    const filtered = users.filter(
      (user) =>
        user.username?.toLowerCase().includes(term.toLowerCase()) ||
        user.deposit.transactionId?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, 300); // Delay of 300ms

  // Handle input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    handleSearch(event.target.value); // Call debounce search
  };

  // Ensure that `filteredUsers` is available before rendering
  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-800 text-white p-6">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard (With Deposits)</h1>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          className="px-4 py-2 rounded-md w-full bg-gray-700 text-white"
          placeholder="Search by username or transaction ID"
          value={searchTerm}
          onChange={handleChange}
        />
      </div>

      {/* User Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <div
              key={user.id}
              className="bg-gray-900 shadow-lg rounded-lg overflow-hidden"
            >
              <div className="p-4">
                <h3 className="font-semibold text-xl">{user.username}</h3>
                <p className="text-gray-400">{user.email}</p>

                <div className="mt-4">
                  <p className="text-gray-500">Deposit Amount: {user.deposit.amount}</p>
                  <p className="text-gray-500">
                    Transaction ID: {user.deposit.transactionId}
                  </p>
                  <p className="text-gray-500">
                    Deposit Date: {user.deposit.date.toDate().toLocaleString()}
                  </p>
                </div>

                <div className="mt-4">
                  <p className="text-gray-500">Status: {user.status}</p>
                  <button
                    onClick={() => toggleVerifyStatus(user.id, user.status)}
                    className={`mt-2 px-4 py-2 rounded-md ${
                      user.status === "verified" ? "bg-red-500" : "bg-green-500"
                    } text-white`}
                  >
                    {user.status === "verified" ? "Unverify" : "Verify"}
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>No users with deposits found.</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardWithDeposits;
